import { Timeline } from '@/utils/date'
import { GenericNode } from '../../types'
import { TimelinePresetOption } from '@/components/common/Filter/types'
import {
    EmissionsScope,
    GetSustainabilityTotalEmissionsRequestParams,
    GHGEmissionScope2Type,
    GHGEmissionsReportSettings,
    TotalEmissionsReportSettings,
    TotalEmissionsReportType,
    TotalEmissionsScope2Type,
} from './useSustainabilityApi'
import {
    DataTypeParam,
    IntervalDataGroupingParam,
    IntervalDataTypeParam,
    IntervalReportSettings,
    IntervalTimeParam,
    GetActiveManagementRequestParams,
} from './useActiveManagementApi'

// Schema for a generic error response
export interface GenericError {
    type: 'error'
    message: string
    details?: Record<string, unknown>
}

// Schema for bad parameters error response
export interface BadParametersError {
    type: 'error' | 'input'
    message: 'Input validation error'
    details?: Record<string, unknown> | unknown[]
}

// Schema for unauthorized error response
export interface UnauthorizedError {
    type: 'error'
    message: 'Unauthorized'
}

// Schema for not found error response
export interface NotFoundError {
    type: 'error'
    message: 'Entity not found'
}

// Schema for forbidden error response
export interface ForbiddenError {
    type: 'error'
    message: 'Forbidden'
}

// Schema for bad output error response
export interface BadOutputError {
    type: 'error'
    message: 'Output validation error'
    details?: Record<string, unknown>
}

// Schema for creating a session request body
export interface CreateSessionRequestBody {
    username: string
    password: string
}

// Schema for a session object
export interface Session {
    sessionId: string
    accessToken: string
    refreshToken: string
}

export type Uuid = `${string}-${string}-${string}-${string}-${string}`

// Schema for a user object
export interface User {
    id: Uuid
    firstName: string | null
    lastName: string | null
    email: string | null
    userName?: string | null
    type: UserType
    contactId?: number
    lastPasswordChangedDate?: string | null // Date-time format
    lastModified?: string | null // Date-time format
    lastLoginIp?: string | null
    lastLoginDate?: string | null // Date-time format
    isloggedIn?: boolean | null
    isBackendUser?: boolean | null
    isApproved?: boolean | null
    creationDate?: string | null // Date-time format
    appName?: string | null
    isDSA?: boolean
    isDeleted?: boolean
    isSendEmail?: boolean
    emailTemplateTypeId?: number | null
    shouldForceReset?: boolean
    failedLoginAttempt?: number | null
    lastLockDateTime?: string | null // Date-time format
    is2FAEnable?: boolean
    isTermsAndConditionsAccepted?: boolean | null
}

export enum UserType {
    AGENT = 'agent',
    BROKER = 'broker',
    CUSTOMER = 'customer',
}

export enum CustomerType {
    AGENCY = 'agency',
    BROKER = 'broker',
    CUSTOMER = 'customer',
}

// Parameters
export interface AuthorizationHeader {
    Authorization: string
}

export interface SessionIdParam {
    sessionId: string
}

export type UsageCostRequestParams = {
    // nodeIds is optional since there's not a default value we could use and we need to wait for /nodes response
    'nodeIds[]'?: string
    commodityTypeId?: string
    startDate: string
    endDate: string

    'providerIds[]'?: string
    'contractIds[]'?: string
    'panIds[]'?: string
    'tagIds[]'?: string
    'facilityIds[]'?: string
    'usnAccountIds[]'?: string
    'meterIds[]'?: string
    unitOfMeasureId?: string
}

export type InvoicesSummaryRequestParams = {
    'nodeIds[]'?: string
    commodityTypeId?: string
    'usnAccountIds[]'?: string
    unitOfMeasureId?: string
    currencyId?: string
}

export type SaveReportRequestParams = PaginationAndSortingParams & {
    nodeIds: number[]
    timeline?: Timeline | TimelinePresetOption
    startDate?: string
    endDate?: string

    commodityTypeId?: number
    commodityTypeIds?: number[]

    providerIds?: number[]
    contractIds?: number[]
    panIds?: number[]
    tagIds?: number[]
    facilityIds?: number[]
    usnAccountIds?: number[]
    meterIds?: number[]
    subMeterIds?: number[]
    invoiceNumbers?: string[]
    unitOfMeasureId?: number

    type?: BenchmarkingDisplay | IntervalDataTypeParam
    trendType?: BenchmarkingAnalysis
    comparisonPeriod?: ComparisonPeriod
    applyWeatherNormalization?: boolean
    applySquareFootage?: boolean
    includeEmissions?: boolean

    dataGrouping?: IntervalDataGroupingParam
    timeInterval?: IntervalTimeParam
    timezone?: string
    dataType?: DataTypeParam
}

export type Commodity = {
    commodityName: string
    commodityTypeId: number
    unitsOfMeasure: UnitOfMeasure[]
}

export type UnitOfMeasure = {
    unitOfMeasureId: number
    unit: string
    isStandardUnit?: boolean
}

export type Calculation =
    | 'productionMonth'
    | 'calendarMonth'
    | 'accountingMonth'

export type UsageCostResponseBody = {
    totalItems: number
    totals: UsageCostEntry
    items: UsageCostAugmentedEntry[]
}

export type UsageCostAugmentedEntry = UsageCostEntry & {
    date: number
    formattedDate: string
    reportedUsns: number
}

export type UsageCostEntry = {
    usage: {
        commodityUsage?: number
        baselineUsage?: number
        transportUsage?: number
        weatherNormalized?: number
        budget?: number
        unit: string
    }
    cost: {
        totalInvoiced?: number
        commodity?: {
            totalCommodityCost?: number
            supply?: number
            transmission?: number
            capacity?: number
        }
        transport?: {
            totalTransportCost?: number
            tariff?: number
            '4cp4ncp'?: number
            billingDemand?: number
        }
        taxes?: number
        other?: number
        baselineCost?: number
        budget?: number
        unit: string
    }
    demand: {
        actual?: number
        billing?: number
        unit: string
    }
}
export type UsageCostAugmentedEntryFlat = UsageCostEntryFlat & {
    date: number
    formattedDate: string
    reportedUsns: number
}

export type UsageCostEntryFlat = {
    usage_commodityUsage?: number
    usage_baselineUsage?: number
    usage_transportUsage?: number
    usage_budget?: number
    usage_weatherNormalized?: number
    usage_unit: string

    cost_totalInvoiced?: number
    cost_commodity_totalCommodityCost?: number
    cost_commodity_supply?: number
    cost_commodity_transmission?: number
    cost_commodity_capacity?: number
    cost_transport_totalTransportCost?: number
    cost_transport_tariff?: number
    cost_transport_4cp4ncp?: number
    cost_transport_billingDemand?: number
    cost_taxes?: number
    cost_other?: number
    cost_baselineCost?: number
    cost_budget?: number
    cost_unit: string
    demand_actual?: number
    demand_billing?: number
    demand_unit: string
}

export type Payable = {
    invoiceId: string
    invoiceNo: string
    invoiceDocId?: number | null
    providerName: string
    billDate: number
    dueDate: number
    billsCount: number
    totalInvoiced: {
        value: number
        unit: string
    }
    charges: {
        value: number
        unit: string
    }
}

export type GetPayablesSummaryResponseBody = {
    items: Array<Payable>
    totalItems: number
}

export type GetPayablesSummaryRequestParams = PaginationAndSortingParams & {
    'nodeIds[]'?: string
    'commodityTypeIds[]'?: string
    'providerIds[]'?: string
    'contractIds[]'?: string
    'panIds[]'?: string
    'tagIds[]'?: string
    'facilityIds[]'?: string
    'usnAccountIds[]'?: string
    'invoiceNumbers[]'?: string
    startDate: string
    endDate: string
}

export type UsageCostDataSet = Exclude<
    keyof UsageCostAugmentedEntryFlat,
    | 'formattedDate'
    | 'cost_unit'
    | 'usage_unit'
    | 'demand_unit'
    | 'reportedUsns'
>

type UsageCostLabelsMapKey =
    | UsageCostDataSet
    | 'usage'
    | 'cost'
    | 'cost_commodity'
    | 'cost_transport'
    | 'cost_totalInvoiced_noUnit'
    | 'demand'

export type UsageCostLabelsMap = Record<UsageCostLabelsMapKey, string>

export type Customer = {
    configs: {
        baseYearStart: string
        baseYearEnd: string
    }
    customerId: number
    customerName: string
    type?: CustomerType
    contactId?: number
}

export type Node = GenericNode<{
    nodeName: string
    nodeId: number
}>
export type NodesResponse = {
    nodes: Node[]
}

export type Account = {
    panId: number
    panNumber: string
}

export type Facility = {
    facilityId: number
    facilityName: string
    facilityAddress: FacilityAddress
}

export type FacilityAddress = {
    address1: string
    address2?: string
    city?: string
    state?: string
    country?: string
    zip?: string
}

export type Tag = {
    tagId: number
    tagName: string
}

export type Provider = {
    providerId: number
    providerName: string
}

export type Meter = {
    meterId: number
    meterName: string
    meterNumber: string
    facilityName: string
    facilityAddress: FacilityAddress
}

export type Submeter = {
    meterId: number
    meterName: string
    facilityName: string
    facilityAddress: FacilityAddress
}

export type USNAccount = {
    usnAccountId: number
    usnAccountName: string
    usnAccountMeta: string
    facilityAddress: FacilityAddress
}

export type Contract = {
    contractId: number
    contractNumber: string
}

export type InvoiceDetailsResponse = {
    invoiceNo: string
    invoiceDocId?: number | null
    accountNo?: string
    commodities?: Commodity[]
    providers?: Provider[]
    invoiceDate?: number
    submitDate?: number
    dueDate?: number
    invoiceTotal?: AmountWithUnit
    currentCharges?: AmountWithUnit
    balanceForward?: AmountWithUnit
    lateFee?: AmountWithUnit
}

export type AmountWithUnit = {
    value: number
    unit: string
}

export type GetInvoiceBillsResponseBody = {
    items: Array<InvoiceBill>
    totalItems: number
}

export type InvoiceBill = {
    billId: number
    usn: string
    usnAccountName: string
    meters: Meter[]
    startDate: string
    endDate: string
    commodity: Commodity
    usage: AmountWithUnit
    amount: AmountWithUnit
    notesCount: number
}

export type GetInvoiceBillsRequestParams = PaginationAndSortingParams

export type GetBillNotesRequestParams = PaginationAndSortingParams

export type PaginationAndSortingParams = {
    offset?: number
    limit?: number
    order?: string
    sortBy?: string
}

export type GetBillNotesResponse = {
    items: Array<BillNote>
    totalItems: number
}

export type BillNote = {
    billnoteId: number
    note: string
    createdBy?: string
    createdAt?: number
    modifiedBy?: string
    modifiedAt?: number
}

export type BenchmarkingReportSettingsParams = {
    basis?: BenchmarkingBasis
}

export type SaveReportRequestBody = {
    name: string
    type: ReportType
    filters: SaveReportRequestParams | CustomReportFilters
    settings?:
        | BenchmarkingReportSettingsParams
        | TotalEmissionsReportSettings
        | GHGEmissionsReportSettings
        | Omit<IntervalReportSettings, 'display'>
    fields: string[]
}

export type UpdateReportRequestBody = {
    name: string
    type: ReportType
    filters: CustomReportFilters
    settings?:
        | BenchmarkingReportSettingsParams
        | TotalEmissionsReportSettings
        | GHGEmissionsReportSettings
        | Omit<IntervalReportSettings, 'display'>
    fields: string[]
}

export type ReportType =
    | 'usage-cost'
    | 'payables-summary'
    | 'benchmarking'
    | 'total-emissions'
    | 'ghg-emissions'
    | 'interval-data'

export type ReportsItem = {
    customReportId: string
    name: string
    owner: {
        userId: string
        name: string
    }
    type: ReportType
    updatedAt?: number
}

export type GetReportsRequestParams = PaginationAndSortingParams

export type GetReportsResponse = {
    items: Array<ReportsItem>
    totalItems: number
}

export type GetReportResponse = {
    settings?:
        | BenchmarkingReportSettingsParams
        | TotalEmissionsReportSettings
        | GHGEmissionsReportSettings
        | IntervalReportSettings
    customReportId: string
    customerId: number
    owner: {
        userId: string
        name: string
    }
    access: string
    createdAt: string
    updatedAt: string
    name: string
    type: ReportType
    filters: CustomReportFiltersParams
    fields: string[]
}

export type CustomReportFiltersParams =
    | CustomReportFiltersWithFixedDates
    | CustomReportFiltersWithFlexibleDates

export type CustomReportFilters = {
    nodeIds: number[]
    invoiceNumbers?: string[]
    commodityTypeId?: number
    commodityTypeIds?: number[]
    providerIds?: number[]
    contractIds?: number[]
    panIds?: number[]
    tagIds?: number[]
    facilityIds?: number[]
    usnAccountIds?: number[]
    meterIds?: number[]
    subMeterIds?: number[]
    unitOfMeasureId?: number

    type?:
        | BenchmarkingDisplay
        | TotalEmissionsReportType
        | IntervalDataTypeParam
    scope?: EmissionsScope
    scope2type?: TotalEmissionsScope2Type | GHGEmissionScope2Type
    showTotals?: boolean
    trendType?: BenchmarkingAnalysis
    comparisonPeriod?: ComparisonPeriod
    applyWeatherNormalization?: boolean
    applySquareFootage?: boolean
    includeEmissions?: boolean

    dataGrouping?: IntervalDataGroupingParam
    timeInterval?: IntervalTimeParam
    timezone?: string
    dataType?: DataTypeParam
}

export type CustomReportFiltersWithFixedDates = CustomReportFilters & {
    startDate: string
    endDate: string
}

export type CustomReportFiltersWithFlexibleDates = CustomReportFilters & {
    timeline: TimelinePresetOption
}

export type UpdateReportResponse = GetReportResponse

export type GetReportExportDownloadResponse = {
    downloadUrl: string
}

export type GetInvoiceNumbersResponse = {
    invoiceNo: string[]
}

export type UsageCostReport = GetReportResponse & {
    type: 'usage-cost'
    filters: UsageCostRequestParams
}

export type PayablesSummaryReport = GetReportResponse &
    PaginationAndSortingParams & {
        type: 'payables-summary'
        filters: GetPayablesSummaryRequestParams
    }

export type BenchmarkingReport = GetReportResponse & {
    type: 'benchmarking'
    filters: BenchmarkingRequestParams
}

export type TotalEmissionsReport = GetReportResponse & {
    type: 'total-emissions'
    filters: GetSustainabilityTotalEmissionsRequestParams
}

export type IntervalDataReport = GetReportResponse & {
    type: 'interval-data'
    filters: GetActiveManagementRequestParams
}

export type ReportResponse =
    | UsageCostReport
    | PayablesSummaryReport
    | BenchmarkingReport
    | TotalEmissionsReport

export type BenchmarkingDisplay =
    | 'node'
    | 'facility'
    | 'group'
    | 'usn'
    | 'meter'

export type BenchmarkingItemWithUnit = BenchmarkingItem & {
    cost: {
        currency: string
    }
    usage: {
        unit: string
    }
}

export type BenchmarkingBasis = 'usage' | 'cost' | 'costPerUsage' | 'emission'

export type BenchmarkingAnalysis = 'trend' | 'average' | 'totals'

export type ComparisonPeriod = 'previousYear' | 'baselineYear'

export type BenchmarkingReportSettings = {
    display: BenchmarkingDisplay
    basis: BenchmarkingBasis
    analysisType: BenchmarkingAnalysis
    comparisonPeriod?: ComparisonPeriod
    applyWeatherNormalization: boolean
    applySquareFootage: boolean
}

export interface BenchmarkingRequestParams {
    'nodeIds[]'?: string
    commodityTypeId?: string
    unitOfMeasureId?: string
    startDate: string
    endDate: string

    'providerIds[]'?: string
    'contractIds[]'?: string
    'panIds[]'?: string
    'tagIds[]'?: string
    'facilityIds[]'?: string
    'usnAccountIds[]'?: string
    'meterIds[]'?: string
    currencyIds?: number

    type?: BenchmarkingDisplay
    trendType?: BenchmarkingAnalysis
    comparisonPeriod?: ComparisonPeriod
    applyWeatherNormalization?: boolean
    applySquareFootage?: boolean
    includeEmissions?: boolean
}

export type BenchmarkingItem = {
    billDate?: string
    entityId: number
    entityName: string
    usage: {
        current: number
        baseYear?: number
        previousYear?: number
        unitId: number
    }
    cost: {
        current: number
        baseYear?: number
        previousYear?: number
        currencyId: number
    }
    costPerUsage?: {
        current: number
        baseYear?: number
        previousYear?: number
    }
    emission?: {
        current: number
        baseYear?: number
        previousYear?: number
        unit: string
    }
    squareFootage: number
}

export type BenchmarkingResponse = BenchmarkingItem[]

export type DateRange = {
    startDate: string
    endDate: string
}

export type InvoiceSummary = {
    usage: AmountWithUnit
    cost: AmountWithUnit
    count: number
    dateRange: DateRange
}

export type GetInvoicesSummaryResponseBody = {
    currentYear: InvoiceSummary
    lastYear: InvoiceSummary
}

type RecentCustomer = {
    recentCustomerId: string
    userId: string
    customer: {
        customerId: number
        customerName: string
    }
    accessedAt: string
}

export type Permission = {
    id: string
    name: string
}

export type GetRolesAndPermissionsResponse = {
    id: string
    name: string
    permissions: Permission[]
}

export type GetRecentCustomersResponse = Array<RecentCustomer>

export type EnergySourceType = 'RenewableEnergy' | 'Electric' | 'Steam'

export type EnergySourceAndContract = {
    id: number
    name: string
    type: EnergySourceType
    usnCount: number
    startDate: string
    endDate: string
    lastUpdate: string
    updatedBy?: string
    contactId?: number
}

export type GetEnergySourcesAndContractsRequestParams =
    PaginationAndSortingParams & {
        customerId: string
        startDate?: string
        endDate?: string
        'usnAccountIds[]'?: string
        'sourceTypes[]'?: string
    }

export type EnergySourcesAndContractsResponse = {
    items: Array<EnergySourceAndContract>
    totalItems: number
}
